// ============================================================
// OverviewSheet - the harmonized character action sheet used by
// BOTH panels' Overview tabs (v6.33.0). Section order:
//   1. Test difficulty (separate card - governs every test)
//   2. Attributes (clickable grid + movement values)
//   3. Dice Tray
//   4. Skills (full catalog, clickable, parry weapon selector)
//   5. Combat Rules
//   6. Active Talents (with the granted-by-trait group)
//   7. Traits
// Presentational: contexts stay split - each panel feeds its own
// roll callbacks. The defender passes its live `target` (trait
// filtering + live combat rules); the attacker derives rule flags
// from the character via characterToTarget (memoized).
// ============================================================
import { memo, useMemo, useState } from 'react';
import styles from '../../styles/OverviewSheet.module.css';
import { DEFAULT_DIFFICULTY_ID, getDifficulty, SKILL_TEST_DEFS } from '../../engine/characteristicTests';
import type { SkillTestDef } from '../../engine/characteristicTests';
import { deriveCombatRules } from '../../engine/combatRules';
import { getConditionTestModifier, describeConditionModifiers } from '../../engine/conditionModifiers';
import { characterToTarget } from '../../engine/factories';
import { characteristicBonus } from '../../engine/character/combatStatsResolver';
import DifficultySelector from '../DifficultySelector';
import CharacteristicTestGrid from '../CharacteristicTestGrid';
import type { UnnaturalOverrides } from '../CharacteristicTestGrid';
import SkillsCard from './SkillsCard';
import TalentsCard from './TalentsCard';
import TraitsCard from './TraitsCard';
import CombatRulesCard from './CombatRulesCard';
import DiceTray from '../DiceTray';
import type { Character, CharacteristicId } from '../../engine/character/types';
import type { DiceResult, Target } from '../../types';
import type { ConditionModOptions } from '../../engine/conditionModifiers';

interface OverviewSheetProps {
  character: Character;
  /** Live target (defender side) - trait filtering + live combat-rule flags. */
  target?: Target | null;
  onRollCharacteristic: (
    charId: CharacteristicId,
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => void;
  onRollSkill: (
    skillId: SkillTestDef['id'],
    difficultyMod: number,
    customMod: number,
    extraMod?: { label: string; value: number },
  ) => void;
  onRollDiceExpr: (expr: string) => DiceResult | null;
  /** Right-click opposed rolls (v8.220.0). Absent = right-click inert. */
  onOpposedCharacteristic?: ((charId: CharacteristicId) => boolean) | null;
  onOpposedSkill?: ((skillId: SkillTestDef['id']) => boolean) | null;
  unnaturalOverrides?: UnnaturalOverrides;
  onUnnaturalOverride?: (charId: CharacteristicId, multiplier: number | null) => void;
  /** GM house rule: fold the target's active conditions into every test on this sheet. */
  applyConditionModifiers?: boolean;
  /** Which condition modifiers the table has switched off. */
  condModOptions?: ConditionModOptions;
  /** Skills a condition forbids, keyed by id; the value is the reason. */
  blockedTests?: Record<string, string>;
}

type ExtraMod = { label: string; value: number };

/** Merge two labeled modifiers into one (extraMod is a single line item). */
function mergeExtraMods(a?: ExtraMod, b?: ExtraMod): ExtraMod | undefined {
  if (!a) return b;
  if (!b) return a;
  return { label: `${a.label} · ${b.label}`, value: a.value + b.value };
}

const MOVEMENT_LABELS = ['Half', 'Full', 'Charge', 'Run'] as const;

/** A condition total as a labelled extra, or nothing when it is zero. */
function asExtra({ total, labels }: { total: number; labels: string[] }): ExtraMod | undefined {
  return total !== 0 ? { label: labels.join(' · '), value: total } : undefined;
}

/** A skill's condition extra: its governing characteristic's rules and its own. */
function skillConditionExtra(target: Target, skillId: SkillTestDef['id'], options?: ConditionModOptions) {
  const governing = SKILL_TEST_DEFS.find((d) => d.id === skillId)?.governing;
  if (!governing) return undefined;
  return asExtra(getConditionTestModifier(target, { characteristic: governing, skill: skillId }, options));
}

/** Every skill's non-zero condition total, keyed by id. */
function skillConditionTotals(target: Target, options?: ConditionModOptions): Record<string, number> {
  const out: Record<string, number> = {};
  for (const def of SKILL_TEST_DEFS) {
    const { total } = getConditionTestModifier(target, { characteristic: def.governing, skill: def.id }, options);
    if (total !== 0) out[def.id] = total;
  }
  return out;
}

/** The labelled extra for a characteristic roll and for a skill roll; none while `live` is null. */
function conditionExtras(live: Target | null, options: ConditionModOptions | undefined) {
  return {
    charExtra: (charId: CharacteristicId): ExtraMod | undefined =>
      live ? asExtra(getConditionTestModifier(live, { characteristic: charId }, options)) : undefined,
    skillExtra: (skillId: SkillTestDef['id']): ExtraMod | undefined =>
      live ? skillConditionExtra(live, skillId, options) : undefined,
  };
}

/**
 * Condition modifiers (defender-side house rule): active only with the
 * rule on AND a live target. Gives the summary lines, the labelled extra
 * for a characteristic or a skill roll, and the per-skill totals the
 * rows SHOW - the same numbers handed to the roll. Displaying the
 * sheet's own value while rolling against something else is what made a
 * crippled leg look like it did nothing.
 */
function useConditionMods(target: Target | null, apply: boolean, options: ConditionModOptions | undefined) {
  // The target the modifiers read, or null while they are off.
  const live = apply ? target : null;
  const summary = useMemo(() => (live ? describeConditionModifiers(live, options) : []), [live, options]);
  const skillMods = useMemo(() => (live ? skillConditionTotals(live, options) : {}), [live, options]);
  return { active: !!live, summary, skillMods, ...conditionExtras(live, options) };
}

type ConditionMods = ReturnType<typeof useConditionMods>;

/** Condition modifiers summary - read-only; auto-applied to tests below. */
function ConditionSummary({ lines }: { lines: string[] }) {
  return (
    <div className={styles.card} data-testid="condition-modifiers-card">
      <div className={styles.cardTitle}>Active Condition Modifiers</div>
      <ul className={styles.conditionList}>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/** Older stored characters may lack movement - derive from AB like the editor. */
function MovementRow({ character }: { character: Character }) {
  const ab = characteristicBonus(character.characteristics.agi.value);
  const movement = character.movement || { half: ab, full: ab * 2, charge: ab * 3, run: ab * 6 };
  const movementValues = [movement.half, movement.full, movement.charge, movement.run];
  return (
    <div className={styles.movementRow}>
      {MOVEMENT_LABELS.map((label, i) => (
        <span key={label} className={styles.movementLabel}>
          {label}: <span className={styles.movementValue}>{movementValues[i]}</span>
        </span>
      ))}
    </div>
  );
}

interface DifficultyProps {
  difficultyId: string;
  customMod: number;
  onDifficulty: (id: string) => void;
  onCustomMod: (mod: number) => void;
}

/** 1. Test difficulty - separated: governs every test below. */
function DifficultyCard({ difficultyId, customMod, onDifficulty, onCustomMod }: DifficultyProps) {
  return (
    <div className={styles.difficultyCard} data-testid="difficulty-card">
      <div className={styles.cardTitle}>Test Difficulty</div>
      <DifficultySelector
        difficultyId={difficultyId}
        customMod={customMod}
        onDifficultyChange={onDifficulty}
        onCustomModChange={onCustomMod}
      />
      <div className={styles.difficultyCaption}>Applies to every test in this tab.</div>
    </div>
  );
}

type AttributesProps = Pick<
  OverviewSheetProps,
  'character' | 'onOpposedCharacteristic' | 'unnaturalOverrides' | 'onUnnaturalOverride'
> & { onRoll: (charId: CharacteristicId) => void };

/** 2. Attributes - clickable grid + movement values. */
function AttributesCard({
  character,
  onOpposedCharacteristic,
  unnaturalOverrides,
  onUnnaturalOverride,
  onRoll,
}: AttributesProps) {
  return (
    <div className={styles.card} data-testid="attributes-card">
      <div className={styles.cardTitle}>Attributes</div>
      <CharacteristicTestGrid
        character={character}
        onRoll={onRoll}
        onOpposed={onOpposedCharacteristic}
        editableUnnatural
        unnaturalOverrides={unnaturalOverrides}
        onOverrideChange={onUnnaturalOverride}
      />
      <MovementRow character={character} />
    </div>
  );
}

/** 3. Dice Tray. */
function DiceTrayCard({ onRollExpr }: { onRollExpr: OverviewSheetProps['onRollDiceExpr'] }) {
  return (
    <div className={styles.card} data-testid="dice-tray-card">
      <div className={styles.cardTitle}>Dice Tray</div>
      <DiceTray onRollExpr={onRollExpr} />
    </div>
  );
}

type SkillsSectionProps = Pick<OverviewSheetProps, 'character' | 'blockedTests' | 'onOpposedSkill'> & {
  conditions: ConditionMods;
  /** The roll, its extra already merged with the condition's. */
  onRoll: (skillId: SkillTestDef['id'], extraMod?: ExtraMod) => void;
};

/** 4. Skills - full catalog, clickable; a condition's extra joins the card's own (the parry weapon). */
function SkillsSection({ character, blockedTests, onOpposedSkill, conditions, onRoll }: SkillsSectionProps) {
  return (
    <div className={styles.card} data-testid="skills-card">
      <div className={styles.cardTitle}>Skills</div>
      <SkillsCard
        blocked={blockedTests}
        conditionMods={conditions.skillMods}
        character={character}
        weapons={character.weapons}
        onOpposed={onOpposedSkill}
        onRoll={(skillId, extraMod) => onRoll(skillId, mergeExtraMods(extraMod, conditions.skillExtra(skillId)))}
      />
    </div>
  );
}

function OverviewSheet({
  character,
  target = null,
  onRollCharacteristic,
  onRollSkill,
  onRollDiceExpr,
  onOpposedCharacteristic = null,
  onOpposedSkill = null,
  unnaturalOverrides,
  onUnnaturalOverride,
  applyConditionModifiers = false,
  condModOptions,
  blockedTests,
}: OverviewSheetProps) {
  const [difficultyId, setDifficultyId] = useState(DEFAULT_DIFFICULTY_ID);
  const [customMod, setCustomMod] = useState(0);
  const difficultyMod = getDifficulty(difficultyId)?.mod ?? 0;

  // Combat rules: the defender's live target wins; the attacker derives
  // flags from the character (grants resolve through the same pipeline).
  const rules = useMemo(() => deriveCombatRules(target ?? characterToTarget(character)), [target, character]);
  const conditions = useConditionMods(target, applyConditionModifiers, condModOptions);

  return (
    <>
      <DifficultyCard
        difficultyId={difficultyId}
        customMod={customMod}
        onDifficulty={setDifficultyId}
        onCustomMod={setCustomMod}
      />

      {conditions.active && conditions.summary.length > 0 && <ConditionSummary lines={conditions.summary} />}

      <AttributesCard
        {...{ character, onOpposedCharacteristic, unnaturalOverrides, onUnnaturalOverride }}
        onRoll={(charId) => onRollCharacteristic(charId, difficultyMod, customMod, conditions.charExtra(charId))}
      />

      <DiceTrayCard onRollExpr={onRollDiceExpr} />

      <SkillsSection
        {...{ character, blockedTests, onOpposedSkill, conditions }}
        onRoll={(skillId, extraMod) => onRollSkill(skillId, difficultyMod, customMod, extraMod)}
      />

      {/* 5. Combat Rules */}
      <CombatRulesCard rules={rules} />

      {/* 6. Active Talents (own + granted group) */}
      <TalentsCard character={character} />

      {/* 7. Traits */}
      <TraitsCard character={character} target={target} />
    </>
  );
}

export default memo(OverviewSheet);
